import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Truck, Loader2, Save, ArrowLeft, User, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem('occ_token'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    if (!token) { navigate('/spedizione'); return; }
    try {
      const res = await axios.get(`${API}/occasional/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(res.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('occ_token'); navigate('/spedizione'); }
      else toast.error('Impossibile caricare il profilo');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/occasional/customer/profile`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(res.data);
      // refresh cached name/email
      const cust = JSON.parse(localStorage.getItem('occ_customer') || '{}');
      localStorage.setItem('occ_customer', JSON.stringify({ ...cust, first_name: res.data.first_name, email: res.data.email }));
      toast.success('Dati salvati');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0a0a1a] text-white">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-3">
          <Truck className="w-6 h-6 text-blue-400" />
          <h1 className="text-lg font-semibold">I miei dati</h1>
          <button onClick={() => navigate('/le-mie-spedizioni')} className="ml-auto text-sm text-slate-300 hover:text-white flex items-center gap-1 transition-colors" data-testid="back-shipments">
            <ArrowLeft className="w-4 h-4" /> Le mie spedizioni
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {loading || !form ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carico...</div>
        ) : (
          <Card>
            <CardContent className="p-6 sm:p-8 space-y-5" data-testid="profile-form">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => set('tipo', 'privato')} data-testid="profile-tipo-privato"
                  className={`py-2.5 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${form.tipo === 'privato' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>
                  <User className="w-4 h-4" /> Privato
                </button>
                <button type="button" onClick={() => set('tipo', 'azienda')} data-testid="profile-tipo-azienda"
                  className={`py-2.5 rounded-lg text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${form.tipo === 'azienda' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>
                  <Building2 className="w-4 h-4" /> Azienda
                </button>
              </div>

              {form.tipo === 'azienda' ? (
                <>
                  <div>
                    <Label>Ragione sociale</Label>
                    <Input data-testid="profile-company" value={form.company || ''} onChange={(e) => set('company', e.target.value)} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>P.IVA</Label>
                      <Input data-testid="profile-vat" value={form.vat || ''} onChange={(e) => set('vat', e.target.value)} />
                    </div>
                    <div>
                      <Label>Codice SDI</Label>
                      <Input data-testid="profile-sdi" value={form.sdi || ''} onChange={(e) => set('sdi', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>PEC</Label>
                    <Input data-testid="profile-pec" type="email" value={form.pec || ''} onChange={(e) => set('pec', e.target.value)} />
                  </div>
                </>
              ) : (
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input data-testid="profile-codfis" value={form.codfis || ''} onChange={(e) => set('codfis', e.target.value)} />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nome / Referente</Label>
                  <Input data-testid="profile-firstname" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input data-testid="profile-lastname" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input value={form.email || ''} disabled className="bg-slate-100" />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input data-testid="profile-phone" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input data-testid="profile-address" value={form.address_1 || ''} onChange={(e) => set('address_1', e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>CAP</Label>
                  <Input data-testid="profile-postcode" value={form.postcode || ''} onChange={(e) => set('postcode', e.target.value)} />
                </div>
                <div>
                  <Label>Città</Label>
                  <Input data-testid="profile-city" value={form.city || ''} onChange={(e) => set('city', e.target.value)} />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input data-testid="profile-state" value={form.state || ''} maxLength={2} onChange={(e) => set('state', e.target.value.toUpperCase())} />
                </div>
              </div>

              <Button className="w-full" onClick={save} disabled={saving} data-testid="profile-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salva dati
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerProfile;
